import { useEffect, useRef, useState } from "react";

/**
 * Drawing canvas overlay.
 * Strokes are stored in normalized [0..1] coordinates so they stay pinned to
 * the video area even when the container resizes.
 *
 * Stroke shapes:
 *  { type: 'pen',    color, points: [{x,y}, ...] }
 *  { type: 'line',   color, x1, y1, x2, y2 }
 *  { type: 'arrow',  color, x1, y1, x2, y2 }
 *  { type: 'circle', color, cx, cy, r }
 */
export default function DrawingCanvas({
  strokes,
  setStrokes,
  tool,
  color,
  readOnly = false,
}) {
  const canvasRef = useRef(null);
  const [drafting, setDrafting] = useState(null); // in-progress stroke
  const [size, setSize] = useState({ w: 0, h: 0 });

  // Resize observer: keep canvas pixel size in sync with its CSS box
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setSize({ w: Math.floor(width), h: Math.floor(height) });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Redraw whenever strokes/size/draft change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.w === 0 || size.h === 0) return;
    canvas.width = size.w;
    canvas.height = size.h;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, size.w, size.h);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    const toPx = (p) => ({ x: p.x * size.w, y: p.y * size.h });
    const draw = (s) => {
      ctx.strokeStyle = s.color;
      ctx.fillStyle = s.color;
      ctx.lineWidth = 3;
      if (s.type === "pen") {
        ctx.beginPath();
        s.points.forEach((p, i) => {
          const { x, y } = toPx(p);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.stroke();
      } else if (s.type === "line") {
        const a = toPx({ x: s.x1, y: s.y1 });
        const b = toPx({ x: s.x2, y: s.y2 });
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      } else if (s.type === "arrow") {
        const a = toPx({ x: s.x1, y: s.y1 });
        const b = toPx({ x: s.x2, y: s.y2 });
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        const angle = Math.atan2(b.y - a.y, b.x - a.x);
        const head = 14;
        ctx.beginPath();
        ctx.moveTo(b.x, b.y);
        ctx.lineTo(
          b.x - head * Math.cos(angle - Math.PI / 6),
          b.y - head * Math.sin(angle - Math.PI / 6)
        );
        ctx.lineTo(
          b.x - head * Math.cos(angle + Math.PI / 6),
          b.y - head * Math.sin(angle + Math.PI / 6)
        );
        ctx.closePath();
        ctx.fill();
      } else if (s.type === "circle") {
        const c = toPx({ x: s.cx, y: s.cy });
        const r = s.r * Math.min(size.w, size.h);
        ctx.beginPath();
        ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
        ctx.stroke();
      }
    };
    strokes.forEach(draw);
    if (drafting) draw(drafting);
  }, [strokes, drafting, size]);

  if (readOnly) {
    return (
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none z-10"
      />
    );
  }

  const getNorm = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  };

  const handleDown = (e) => {
    e.preventDefault();
    const p = getNorm(e);
    if (tool === "pen") {
      setDrafting({ type: "pen", color, points: [p] });
    } else if (tool === "line" || tool === "arrow") {
      setDrafting({ type: tool, color, x1: p.x, y1: p.y, x2: p.x, y2: p.y });
    } else if (tool === "circle") {
      setDrafting({ type: "circle", color, cx: p.x, cy: p.y, r: 0 });
    }
  };

  const handleMove = (e) => {
    if (!drafting) return;
    const p = getNorm(e);
    if (drafting.type === "pen") {
      setDrafting({ ...drafting, points: [...drafting.points, p] });
    } else if (drafting.type === "line" || drafting.type === "arrow") {
      setDrafting({ ...drafting, x2: p.x, y2: p.y });
    } else if (drafting.type === "circle") {
      const dx = p.x - drafting.cx;
      const dy = p.y - drafting.cy;
      const r = Math.sqrt(
        (dx * size.w) ** 2 + (dy * size.h) ** 2
      ) / Math.min(size.w, size.h);
      setDrafting({ ...drafting, r });
    }
  };

  const handleUp = () => {
    if (!drafting) return;
    // Discard tiny accidental taps
    let keep = true;
    if (drafting.type === "pen" && drafting.points.length < 2) keep = false;
    if (
      (drafting.type === "line" || drafting.type === "arrow") &&
      Math.hypot(drafting.x2 - drafting.x1, drafting.y2 - drafting.y1) < 0.01
    )
      keep = false;
    if (drafting.type === "circle" && drafting.r < 0.01) keep = false;

    if (keep) setStrokes((prev) => [...prev, drafting]);
    setDrafting(null);
  };

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full z-20 cursor-crosshair touch-none"
      onPointerDown={handleDown}
      onPointerMove={handleMove}
      onPointerUp={handleUp}
      onPointerLeave={handleUp}
    />
  );
}
